import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { styles } from '../../../styles/themes/style'
import { ONBOARDING_STEPPER_BREAKPOINT } from '../onboardingLayoutConstants'

const activeRingPulse = keyframes`
    0%,
    100% {
        transform: scale(1);
        opacity: 1;
        box-shadow: 0 0 0 0 rgba(89, 100, 255, 0.2);
    }
    50% {
        transform: scale(1.1);
        opacity: 0.88;
        box-shadow: 0 0 0 4px rgba(89, 100, 255, 0);
    }
`

const checkAppear = keyframes`
    0% {
        transform: scale(0.4);
        opacity: 0;
    }
    60% {
        transform: scale(1.12);
        opacity: 1;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
`

const checkDraw = keyframes`
    0% {
        stroke-dashoffset: 20;
        opacity: 0.6;
    }
    100% {
        stroke-dashoffset: 0;
        opacity: 1;
    }
`

export type OnboardingStepperStep = {
    id: string
    label: string
}

type StepStatus = 'completed' | 'active' | 'pending'

interface IOnboardingStepperProps {
    steps: OnboardingStepperStep[]
    activeStepIndex: number
}

const OnboardingStepper: React.FC<IOnboardingStepperProps> = ({ steps, activeStepIndex }) => {
    const getStatus = (index: number): StepStatus => {
        if (index < activeStepIndex) return 'completed'
        if (index === activeStepIndex) return 'active'
        return 'pending'
    }

    return (
        <Container aria-label="Onboarding progress">
            {steps.map((step, index) => {
                const status = getStatus(index)
                const isLast = index === steps.length - 1
                const connectorCompleted = index < activeStepIndex

                return (
                    <StepBlock key={step.id}>
                        <StepRow>
                            <StepIndicator $status={status}>
                                {status === 'active' && <ActiveOuterRing aria-hidden />}
                                {status === 'completed' && (
                                    <CompletedCircle>
                                        <CheckMark viewBox="0 0 16 16" aria-hidden>
                                            <path
                                                d="M3.9 8.4L6.5 11L12.1 5.4"
                                            />
                                        </CheckMark>
                                    </CompletedCircle>
                                )}
                                {(status === 'active' || status === 'pending') && (
                                    <InnerDot $active={status === 'active'} aria-hidden />
                                )}
                            </StepIndicator>
                            <StepLabel $status={status}>{step.label}</StepLabel>
                        </StepRow>
                        {!isLast && <Connector $completed={connectorCompleted} />}
                    </StepBlock>
                )
            })}
        </Container>
    )
}

export const ONBOARDING_STEPPER_STEPS: OnboardingStepperStep[] = [
    { id: 'extension', label: 'Chrome App' },
    { id: 'linkedin', label: 'LinkedIn profile' },
    { id: 'sync-network', label: 'Sync your network' },
    { id: 'survey', label: 'Quick questions' },
    { id: 'complete', label: 'All set!' },
]

export const ONBOARDING_SKIP_STEPPER_STEPS_BROWSER: OnboardingStepperStep[] = [
    { id: 'browser-setup', label: 'Browser setup' },
    { id: 'survey', label: 'Quick questions' },
    { id: 'complete', label: 'All set!' },
]

export const ONBOARDING_SKIP_STEPPER_STEPS_EXTENSION: OnboardingStepperStep[] = [
    { id: 'extension', label: 'Chrome App' },
    { id: 'survey', label: 'Quick questions' },
    { id: 'complete', label: 'All set!' },
]

export default OnboardingStepper

const Container = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    width: 174px;
    flex-shrink: 0;

    @media (max-width: ${ONBOARDING_STEPPER_BREAKPOINT}px) {
        display: none;
    }
`

const StepBlock = styled.li`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`

const StepRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
`

const StepIndicator = styled.div<{ $status: StepStatus }>`
    position: relative;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ $status }) =>
        $status === 'pending' &&
        css`
            background: ${styles.colors.primary100};
            border-radius: 50%;
        `}
`

const ActiveOuterRing = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${styles.colors.primary200};
    animation: ${activeRingPulse} 2s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const InnerDot = styled.div<{ $active: boolean }>`
    position: relative;
    z-index: 1;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $active }) =>
        $active ? styles.colors.primary600 : styles.colors.disabledState};
`

const CompletedCircle = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${styles.colors.primary600};
    display: flex;
    align-items: center;
    justify-content: center;
`

const CheckMark = styled.svg`
    width: 14px;
    height: 14px;
    animation: ${checkAppear} 0.35s ease-out;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    path {
        fill: none;
        stroke: ${styles.colors.white};
        stroke-width: 2.1;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-dasharray: 20;
        stroke-dashoffset: 20;
        animation: ${checkDraw} 0.42s ease-out 0.05s forwards;
    }

    @media (prefers-reduced-motion: reduce) {
        path {
            animation: none;
            stroke-dashoffset: 0;
        }
    }
`

const Connector = styled.div<{ $completed: boolean }>`
    width: 2px;
    height: 16px;
    margin: 8px 0 8px 9px;
    background: ${({ $completed }) => ($completed ? styles.colors.primary600 : styles.colors.black200)};
`

const StepLabel = styled.span<{ $status: StepStatus }>`
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    color: ${({ $status }) =>
        $status === 'active' ? styles.colors.black400 : styles.colors.disabledState};
    padding-top: 0;
`
